// Claude: Tags List — snapshot cached in sessionStorage; client-side cascading filters 19-05-2026
import React, { useEffect, useState } from "react";
import { Table, Select, Input } from "antd";
import { Button, Flex, Text } from "@chakra-ui/react";
import { MdChevronLeft } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

const { Option } = Select;

const SNAPSHOT_KEY = "aftr_tagsListSnapshot";

const columns = [
  { title: "Tag", dataIndex: "tag", key: "tag", sorter: (a: any, b: any) => a.tag.localeCompare(b.tag), defaultSortOrder: "ascend" as const },
  { title: "Sellers", dataIndex: "sellerCount", key: "sellerCount", sorter: (a: any, b: any) => a.sellerCount - b.sellerCount, align: "center" as const },
];

const TagsList = () => {
  const navigate = useNavigate();
  const { error } = useToastWrapper();

  const [snapshot, setSnapshot] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<{ tagName?: string; country?: string; industryId?: string; targetIndustryId?: string; targetDepartmentId?: string }>({});
  const [options, setOptions] = useState<{ countries: string[]; industries: any[]; targetIndustries: any[]; targetDepartments: any[] }>({ countries: [], industries: [], targetIndustries: [], targetDepartments: [] });
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    const cached = sessionStorage.getItem(SNAPSHOT_KEY);
    if (cached) {
      setSnapshot(JSON.parse(cached));
      setLoading(false);
      return;
    }
    AdminAPI.getTagsListSnapshot()
      .then((res: any) => {
        const snap = res?.data ?? res;
        sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snap));
        setSnapshot(snap);
      })
      .catch((err: any) => error(errorHandler(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!snapshot) return;
    const rows: any[] = snapshot.rows || [];
    const industryMap: Record<string, string> = snapshot.industryMap || {};
    const departmentMap: Record<string, string> = snapshot.departmentMap || {};
    const { country, industryId, targetIndustryId, targetDepartmentId } = filters;

    // cascade: each tier filters on the tiers to its left
    const forIndustry = country ? rows.filter((r: any) => r.country === country) : rows;
    const forTargetIndustry = industryId ? forIndustry.filter((r: any) => (r.industryIds || []).indexOf(industryId) !== -1) : forIndustry;
    const forDept = targetIndustryId ? forTargetIndustry.filter((r: any) => (r.targetIndustryIds || []).indexOf(targetIndustryId) !== -1) : forTargetIndustry;

    // countries
    const countrySet: Record<string, boolean> = {};
    rows.forEach((r: any) => { if (r.country) countrySet[r.country] = true; });
    const countries = Object.keys(countrySet).sort();

    // industries
    const indSet: Record<string, boolean> = {};
    forIndustry.forEach((r: any) => (r.industryIds || []).forEach((id: string) => { indSet[id] = true; }));
    const industries = Object.keys(indSet)
      .filter((id) => industryMap[id])
      .map((id) => ({ _id: id, name: industryMap[id] }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // target industries
    const tiSet: Record<string, boolean> = {};
    forTargetIndustry.forEach((r: any) => (r.targetIndustryIds || []).forEach((id: string) => { tiSet[id] = true; }));
    const targetIndustries = Object.keys(tiSet)
      .filter((id) => industryMap[id])
      .map((id) => ({ _id: id, name: industryMap[id] }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // target departments
    const deptSet: Record<string, boolean> = {};
    forDept.forEach((r: any) => (r.targetDepartmentIds || []).forEach((id: string) => { deptSet[id] = true; }));
    const targetDepartments = Object.keys(deptSet)
      .filter((id) => departmentMap[id])
      .map((id) => ({ _id: id, name: departmentMap[id] }))
      .sort((a, b) => a.name.localeCompare(b.name));

    setOptions({ countries, industries, targetIndustries, targetDepartments });

    // tags: filter rows by all 4 filters
    const filtered = rows.filter((r: any) => {
      if (country && r.country !== country) return false;
      if (industryId && (r.industryIds || []).indexOf(industryId) === -1) return false;
      if (targetIndustryId && (r.targetIndustryIds || []).indexOf(targetIndustryId) === -1) return false;
      if (targetDepartmentId && (r.targetDepartmentIds || []).indexOf(targetDepartmentId) === -1) return false;
      return true;
    });
    const counts: Record<string, number> = {};
    filtered.forEach((r: any) => { (r.tags || []).forEach((tag: string) => { counts[tag] = (counts[tag] || 0) + 1; }); });
    const { tagName } = filters;
    const tagRows = Object.keys(counts)
      .map((tag) => ({ tag, sellerCount: counts[tag], key: tag }))
      .filter((row) => !tagName || row.tag.toLowerCase().includes(tagName.toLowerCase()))
      .sort((a, b) => a.tag.localeCompare(b.tag));

    setData(tagRows);
  }, [snapshot, filters]);

  const handleChange = (field: string, value: string | undefined) => {
    setFilters((prev) => {
      const next: any = { ...prev, [field]: value };
      if (field === "country") { delete next.industryId; delete next.targetIndustryId; delete next.targetDepartmentId; }
      else if (field === "industryId") { delete next.targetIndustryId; delete next.targetDepartmentId; }
      else if (field === "targetIndustryId") { delete next.targetDepartmentId; }
      return next;
    });
  };

  const hasFilters = Object.keys(filters).length > 0;

  return (
    <div className="p-4">
      <Flex align="center" mb={1}>
        <Button size="sm" variant="ghost" leftIcon={<MdChevronLeft size={18} />} onClick={() => navigate("/admin/sellers")} _hover={{ bg: "gray.100" }}>
          Back
        </Button>
        <Text fontSize="2xl" fontWeight={600} ml={2}>Tags List</Text>
      </Flex>

      <Text fontSize="11px" color="gray.400" mb={4} ml={1} fontStyle="italic">
        Filters are cached locally per session.
      </Text>

      <Flex gap={3} mb={3} wrap="wrap" align="flex-end">
        {/* Claude: tag name filter — client-side substring match 20-05-2026 */}
        <div style={{ minWidth: 200 }}>
          <Text fontSize="11px" color="gray.500" mb={1}>Tag Name</Text>
          <Input
            placeholder="Search tags..."
            allowClear
            value={filters.tagName ?? ""}
            onChange={(e) => handleChange("tagName", e.target.value || undefined)}
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ minWidth: 180 }}>
          <Text fontSize="11px" color="gray.500" mb={1}>Country</Text>
          <Select allowClear placeholder="All countries" style={{ width: "100%" }} value={filters.country} onChange={(v) => handleChange("country", v)} showSearch
            filterOption={(input: string, opt: any) => String(opt?.children ?? "").toLowerCase().includes(input.toLowerCase())}>
            {options.countries.map((c) => <Option key={c} value={c}>{c}</Option>)}
          </Select>
        </div>

        <div style={{ minWidth: 200 }}>
          <Text fontSize="11px" color="gray.500" mb={1}>Industry</Text>
          <Select allowClear placeholder="All industries" style={{ width: "100%" }} value={filters.industryId} onChange={(v) => handleChange("industryId", v)} showSearch
            filterOption={(input: string, opt: any) => String(opt?.children ?? "").toLowerCase().includes(input.toLowerCase())}>
            {options.industries.map((o: any) => <Option key={o._id} value={o._id}>{o.name}</Option>)}
          </Select>
        </div>

        <div style={{ minWidth: 200 }}>
          <Text fontSize="11px" color="gray.500" mb={1}>Target Industry</Text>
          <Select allowClear placeholder="All target industries" style={{ width: "100%" }} value={filters.targetIndustryId} onChange={(v) => handleChange("targetIndustryId", v)} showSearch
            filterOption={(input: string, opt: any) => String(opt?.children ?? "").toLowerCase().includes(input.toLowerCase())}>
            {options.targetIndustries.map((o: any) => <Option key={o._id} value={o._id}>{o.name}</Option>)}
          </Select>
        </div>

        <div style={{ minWidth: 200 }}>
          <Text fontSize="11px" color="gray.500" mb={1}>Target Department</Text>
          <Select allowClear placeholder="All departments" style={{ width: "100%" }} value={filters.targetDepartmentId} onChange={(v) => handleChange("targetDepartmentId", v)} showSearch
            filterOption={(input: string, opt: any) => String(opt?.children ?? "").toLowerCase().includes(input.toLowerCase())}>
            {options.targetDepartments.map((o: any) => <Option key={o._id} value={o._id}>{o.name}</Option>)}
          </Select>
        </div>

        {hasFilters && (
          <Button size="sm" variant="ghost" onClick={() => setFilters({})} color="gray.500">
            Clear filters
          </Button>
        )}
      </Flex>

      <Text fontSize="11px" color="gray.500" mb={2} fontStyle="italic">
        {data.length} unique tag{data.length !== 1 ? "s" : ""}. Seller count shows how many sellers have each tag.
      </Text>

      <Table columns={columns} dataSource={data} loading={loading} pagination={false} size="middle" bordered />
    </div>
  );
};

export default TagsList;
